import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authClient } from '@/lib/auth-client'
import { meQuery } from '@/lib/queries'
import { type Theme, useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/$org/settings/account')({
  component: AccountSettingsPage,
})

const THEMES: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

function AccountSettingsPage() {
  const { data } = useQuery(meQuery)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()

  const [name, setName] = useState<string | null>(null)
  const [savingName, setSavingName] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)

  const nameValue = name ?? data?.user.name ?? ''

  async function saveName(event: React.FormEvent) {
    event.preventDefault()
    setSavingName(true)
    const { error } = await authClient.updateUser({ name: nameValue.trim() })
    setSavingName(false)
    if (error) {
      toast.error(error.message ?? 'Could not save')
      return
    }
    await queryClient.invalidateQueries({ queryKey: ['me'] })
    toast.success('Name updated')
  }

  async function changePassword(event: React.FormEvent) {
    event.preventDefault()
    setSavingPassword(true)
    const { error } = await authClient.changePassword({
      currentPassword,
      newPassword,
      // Other sessions are no longer trustworthy once the password changes.
      revokeOtherSessions: true,
    })
    setSavingPassword(false)
    if (error) {
      toast.error(error.message ?? 'Could not change password')
      return
    }
    setCurrentPassword('')
    setNewPassword('')
    toast.success('Password changed')
  }

  return (
    <>
      <PageHeader title="Account" description="Your profile and preferences." />

      <div className="space-y-4">
        <section className="rounded-lg border border-border bg-card p-5">
          <form onSubmit={saveName} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={nameValue}
                onChange={(event) => setName(event.target.value)}
                className="max-w-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={data?.user.email ?? ''}
                readOnly
                disabled
                className="max-w-sm font-mono"
              />
            </div>
            <Button type="submit" size="sm" disabled={savingName || !nameValue.trim()}>
              {savingName ? 'Saving…' : 'Save'}
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="pb-1 text-[13px] font-medium">Theme</h2>
          <p className="pb-4 text-[13px] text-muted-foreground">Applies to this browser only.</p>
          <div className="inline-flex rounded-md border border-border p-0.5">
            {THEMES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setTheme(option.value)}
                className={cn(
                  'rounded-[4px] px-3 py-1 text-[13px] transition-colors',
                  theme === option.value
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="pb-4 text-[13px] font-medium">Change password</h2>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                className="max-w-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                minLength={10}
                className="max-w-sm"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={savingPassword || newPassword.length < 10 || !currentPassword}
            >
              {savingPassword ? 'Changing…' : 'Change password'}
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="pb-1 text-[13px] font-medium">Sign out</h2>
          <p className="pb-4 text-[13px] text-muted-foreground">
            Ends this session on this device.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await authClient.signOut()
              queryClient.clear()
              navigate({ to: '/login' })
            }}
          >
            Sign out
          </Button>
        </section>
      </div>
    </>
  )
}
